import { createBrowserHistory } from "history";
import { parse } from "query-string";
import React, { Fragment } from "react";

export interface RouteProps {
  appId?: string;
  uuid?: string;
  documentType?: string;
}

const UUID_PARAM = ":uuid([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})";
const APP_ID_PARAM = ":appId([0-9]*)";
const BASE_PATH = "/u";
const DOCUMENT_TYPE_PARAM = ":documentType(passport|state_id|driver_license|green_card)";
export class Routes {
  public static BROWSER_HISTORY = createBrowserHistory();
  public static HOME_WITH_LINK = `${BASE_PATH}/${UUID_PARAM}`;
  public static HOME_WITH_SESSION = `${BASE_PATH}/app/${APP_ID_PARAM}`;
  public static HOME_WITH_LINK_DOC = `${BASE_PATH}/${UUID_PARAM}/${DOCUMENT_TYPE_PARAM}`;
  public static HOME_WITH_SESSION_DOC = `${BASE_PATH}/app/${APP_ID_PARAM}/${DOCUMENT_TYPE_PARAM}`;
  /* Note: Routing is nested based on the link or anon paths. So all routes should start with one of these or routing
     should be adjusted. FINISHED is an example of an exception to this rule but its routing is handled separately. */
  public static UI_PATHS = {
    FINISHED: `${BASE_PATH}/finished`,
    LANDING_WITH_LINK: Routes.HOME_WITH_LINK,
    LANDING_WITH_SESSION: Routes.HOME_WITH_SESSION,
    UPLOAD_MANUAL_WITH_LINK: `${Routes.HOME_WITH_LINK}/upload-manual`,
    UPLOAD_MANUAL_WITH_SESSION: `${Routes.HOME_WITH_SESSION}/upload-manual`,
    ESCAPE_HATCH_WITH_LINK: `${Routes.HOME_WITH_LINK}/upload-manual-instead`,
    ESCAPE_HATCH_WITH_SESSION: `${Routes.HOME_WITH_SESSION}/upload-manual-instead`,
    UPLOAD_WITH_LINK: `${Routes.HOME_WITH_LINK}/upload`,
    UPLOAD_WITH_LINK_DEBUG: `${Routes.HOME_WITH_LINK}/upload/debug`,
    UPLOAD_WITH_SESSION: `${Routes.HOME_WITH_SESSION}/upload`,
    UPLOAD_WITH_SESSION_DEBUG: `${Routes.HOME_WITH_SESSION}/upload/debug`,
    LANDING_WITH_LINK_DOC: Routes.HOME_WITH_LINK_DOC,
    LANDING_WITH_SESSION_DOC: Routes.HOME_WITH_SESSION_DOC,
    UPLOAD_MANUAL_WITH_LINK_DOC: `${Routes.HOME_WITH_LINK_DOC}/upload-manual`,
    UPLOAD_MANUAL_WITH_SESSION_DOC: `${Routes.HOME_WITH_SESSION_DOC}/upload-manual`,
    ESCAPE_HATCH_WITH_LINK_DOC: `${Routes.HOME_WITH_LINK}/upload-manual-instead`,
    ESCAPE_HATCH_WITH_SESSION_DOC: `${Routes.HOME_WITH_SESSION_DOC}/upload-manual-instead`,
    UPLOAD_WITH_LINK_DOC: `${Routes.HOME_WITH_LINK_DOC}/upload`,
    UPLOAD_WITH_LINK_DEBUG_DOC: `${Routes.HOME_WITH_LINK_DOC}/upload/debug`,
    UPLOAD_WITH_SESSION_DOC: `${Routes.HOME_WITH_SESSION_DOC}/upload`,
    UPLOAD_WITH_SESSION_DEBUG_DOC: `${Routes.HOME_WITH_SESSION_DOC}/upload/debug`,
  };

  private static setUuid = (text: string, uuid: string): string => {
    return text.replace(UUID_PARAM, `${uuid}`);
  };

  private static setAppId = (text: string, appId: string): string => {
    return text.replace(APP_ID_PARAM, `${appId}`);
  };

  private static setDocumentType = (text: string, documentType: string): string => {
    return text.replace(DOCUMENT_TYPE_PARAM, `${documentType}`);
  };

  private static pushWithExistParams = (pathname: string) => {
    Routes.BROWSER_HISTORY.push({
      pathname,
      search: Routes.BROWSER_HISTORY.location.search,
    });
  };

  public static goToFinished = () => {
    Routes.pushWithExistParams(Routes.UI_PATHS.FINISHED);
  };

  public static goToLanding = (
      uuid: string | undefined, appId: string | undefined, documentType: string | undefined
  ) => {
    if (!!uuid) {
      Routes.pushWithExistParams(
          Routes.setDocumentType(Routes.setUuid(documentType === undefined ?
              Routes.UI_PATHS.LANDING_WITH_LINK : Routes.UI_PATHS.LANDING_WITH_LINK_DOC, uuid), documentType)
      );
    } else if (!!appId) {
      Routes.pushWithExistParams(
          Routes.setDocumentType(Routes.setAppId(documentType === undefined ?
              Routes.UI_PATHS.LANDING_WITH_SESSION : Routes.UI_PATHS.LANDING_WITH_SESSION_DOC, appId), documentType)
      );
    }
  };

  public static goToUpload = (
      uuid: string | undefined, appId: string | undefined, documentType: string | undefined
  ) => {
    if (!!uuid) {
      Routes.pushWithExistParams(
          Routes.setDocumentType(Routes.setUuid(documentType === undefined ?
              Routes.UI_PATHS.UPLOAD_WITH_LINK
              : Routes.UI_PATHS.UPLOAD_WITH_LINK_DOC, uuid), documentType)
      );
    } else if (!!appId) {
      Routes.pushWithExistParams(
          Routes.setDocumentType(Routes.setAppId(documentType === undefined ?
              Routes.UI_PATHS.UPLOAD_WITH_SESSION
              : Routes.UI_PATHS.UPLOAD_WITH_SESSION_DOC, appId), documentType)
      );
    }
  };

  public static goToUploadManual = (
      uuid: string | undefined, appId: string | undefined, documentType: string | undefined
  ) => {
    if (!!uuid) {
      Routes.pushWithExistParams(
          Routes.setDocumentType(Routes.setUuid(documentType === undefined ?
              Routes.UI_PATHS.UPLOAD_MANUAL_WITH_LINK
              : Routes.UI_PATHS.UPLOAD_MANUAL_WITH_LINK_DOC, uuid), documentType)
      );
    } else if (!!appId) {
      Routes.pushWithExistParams(
          Routes.setDocumentType(Routes.setAppId(documentType === undefined ?
              Routes.UI_PATHS.UPLOAD_MANUAL_WITH_SESSION
              : Routes.UI_PATHS.UPLOAD_MANUAL_WITH_SESSION_DOC, appId), documentType)
      );
    }
  };

  public static goToEscapeHatch = (
      uuid: string | undefined, appId: string | undefined, documentType: string | undefined
  ) => {
    if (!!uuid) {
      Routes.pushWithExistParams(
          Routes.setDocumentType(Routes.setUuid(documentType === undefined ?
              Routes.UI_PATHS.ESCAPE_HATCH_WITH_LINK
              : Routes.UI_PATHS.ESCAPE_HATCH_WITH_LINK_DOC, uuid), documentType)
      );
    } else if (!!appId) {
      Routes.pushWithExistParams(
          Routes.setDocumentType(Routes.setAppId(documentType === undefined ?
              Routes.UI_PATHS.ESCAPE_HATCH_WITH_SESSION
              : Routes.UI_PATHS.ESCAPE_HATCH_WITH_SESSION_DOC, appId), documentType)
      );
    }
  };

  public static expiredLinkLoginRedirect = (appId: string, isCoborrower: boolean) => {
    // pushing browser history doesn't hit bouncer, need to window navigate.
    window.location.href = `${Routes.setAppId(Routes.UI_PATHS.LANDING_WITH_SESSION, appId)}?coborrower=${isCoborrower}`;
  };

  public static getReturnDevice = () => {
    const retUrl = Routes.getReturnHref();
    if (!!retUrl) {
      return <a href={retUrl} target="_self">application</a>;
    } else {
      return <Fragment>logged in device</Fragment>;
    }
  };

  public static getReturnHref = (): string | null => {
    const searchParams = parse(Routes.BROWSER_HISTORY.location.search);
    const url = searchParams.retUrl != null ? searchParams.retUrl : searchParams.returl;
    if (!!url) {
      // force a relative link string
      if (!url?.toString().match(/^\/([a-zA-Z0-9\-_]+\/?)*$/)) {
        return null;
      }
    }
    return url?.toString();
  };

  public static isCOBorrower = (): string | null => {
      const isCoBorrower = parse(Routes.BROWSER_HISTORY.location.search).coborrower?.toString();
      return isCoBorrower === "undefined" ? null : isCoBorrower;
  };

  public static BORROWER_HREF = "/b";
}



// WEBPACK FOOTER //
// ./src/constants/Routing.tsx